import {
  Component, ElementRef,
  Input, OnChanges,
  OnInit, Optional, Self, SimpleChanges,
  ViewChild,
} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {LookupModel, TagModel} from '../../api/models';
import { MatFormFieldControl, MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import {MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import { UntypedFormControl, NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {MatBaseInputComponent} from '../mat-base-input.component';
import { MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { MatFormFieldFix } from '../mat-form-field/mat-form-field-fix';
import { NgFor, NgStyle, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatOption } from '@angular/material/core';

@Component({
    selector: 'app-mat-tags-multiselect',
    template: `
    <mat-form-field appearance="outline" class="regular-form-field multiselect" [hintLabel]="hintLabel"
                    (focusin)="onFocusIn($event)"
                    (focusout)="onFocusOut($event)">
      <mat-label>{{ label }}</mat-label>
      <mat-chip-grid #chipList [disabled]="disabled || submitted">
        <mat-chip-row *ngFor="let item of selectedItems"
                      [editable]="!disabled && !submitted"
                      [removable]="!disabled && !submitted"
                      [ngStyle]="{'background-color': item.color, 'color': getColor(item.color)}"
                      (removed)="remove(item)">
          <div class="recipient-title">
            {{ item.name }}
          </div>
          <mat-icon matChipRemove *ngIf="!disabled && !submitted">cancel</mat-icon>
        </mat-chip-row>

        <input class="autocomplete" type="text" #inputTrigger *ngIf="!disabled"
               matInput
               #trigger="matAutocompleteTrigger"
               [matAutocomplete]="autoComplete"
               [formControl]="itemControl"
               [placeholder]="placeholder"
               [matChipInputFor]="chipList"
               [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
               (click)="openSelectOptions($event, trigger)"
        />
      </mat-chip-grid>
      <button mat-icon-button matSuffix type="button" *ngIf="suffixIcon" (click)="onSuffixClicked.emit($event)"
              [matTooltip]="suffixTooltip">
        <mat-icon matSuffix>{{ suffixIcon }}</mat-icon>
      </button>
      <mat-autocomplete #autoComplete="matAutocomplete" (optionSelected)="optionSelected($event)">
        <mat-option *ngFor="let item of items" [value]="item" [disabled]="item.name.indexOf('invalid') > -1">
          {{ item.name }}
        </mat-option>
      </mat-autocomplete>
      <mat-error *ngIf="hasErrors()">{{ firstError() }}</mat-error>
    </mat-form-field>
  `,
    styleUrls: ['./mat-tags-multiselect.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: MatTagsMultiselectComponent }
    ],
    standalone: true,
    imports: [MatFormField, MatFormFieldFix, MatLabel, MatChipGrid, NgFor, MatChipRow, NgStyle, NgIf, MatIcon, MatChipRemove, MatInput, MatAutocompleteTrigger, FormsModule, MatChipInput, ReactiveFormsModule, MatIconButton, MatSuffix, MatTooltip, MatAutocomplete, MatOption, MatError]
})
export class MatTagsMultiselectComponent extends MatBaseInputComponent implements OnInit, OnChanges {

  separatorKeysCodes: number[] = [ENTER, COMMA];

  @ViewChild('autoComplete') autoComplete: MatAutocomplete;
  @ViewChild('trigger') trigger: MatAutocompleteTrigger;
  @ViewChild('chipList') chipList: MatChipGrid;
  @Input() autoCompleteValues: TagModel[] = [];
  @Input() validator: 'none' | 'mail' = 'none';
  @Input() valueField = 'id';
  @Input() allowCustomValue = false;
  @Input() includeSelected = false;

  @Input() set value(value: any) {
    if (value) {
      this.selectedItems = value;
    }
    this.stateChanges.next();
  }

  get value() {
    return this.selectedItems;
  }

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    public elementRef: ElementRef<HTMLElement>,
  ) {
    super(ngControl, elementRef);
  }

  @ViewChild('inputTrigger') inputTrigger: ElementRef<HTMLInputElement>;
  itemControl = new UntypedFormControl();
  items: TagModel[] = [];
  selectedItems: TagModel[] = [];
  lastFilter = '';

  ngOnInit() {
    this.itemControl.valueChanges.pipe(
      startWith<string | LookupModel[]>(''),
      map(value => typeof value === 'string' ? value : this.lastFilter),
      map(searchText => {
        return this.filter(searchText);
      })
    ).subscribe((data => {
      data.subscribe((items) => this.items = items);
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    this.clearInput();
  }

  remove(item: TagModel): void {
    const index = this.selectedItems.findIndex(value => value === item);
    if (index >= 0) {
      this.selectedItems.splice(index, 1);
      this.propagateChange(this.selectedItems);
      this.clearInput();
    }
  }

  filter(searchText: string): Observable<TagModel[]> {
    return of(this.autoCompleteValues.filter(x => {
      const text = (x.name || '').toLowerCase();
      const search = (searchText || '').toLowerCase();
      if (!this.includeSelected) {
        return this.selectedItems.findIndex(e => e.id === x.id) === -1 && text.indexOf(search) > -1;
      }
      return text.indexOf(search) > -1;
    }));
  }

  optionSelected($event: MatAutocompleteSelectedEvent) {
    console.log($event);
    const option: TagModel = $event.option.value;
    const i = this.selectedItems.findIndex(value => value.id === option.id);

    if (i === -1) {
      this.selectedItems.push(option);
      this.propagateChange(this.selectedItems);
    }

    this.clearInput();
  }

  hasErrors() {
    let showErrors = false;
    if (!this.formControl) {
      this.errorState = showErrors;
      return showErrors;
    }

    showErrors = (this.formControl.invalid && (this.formControl.dirty || this.formControl.touched))
      || (this.submitted && this.formControl.invalid);

    this.errorState = showErrors;
    if (this.chipList) {
      this.chipList.errorState = showErrors;
    }

    return showErrors;
  }

  firstError(): string {
    const message = super.firstError();
    if (message) {
      this.hasErrors();
    } else {
      this.errorState = false;
    }
    return message;
  }

  clearInput() {
    if (this.inputTrigger?.nativeElement) {
      this.inputTrigger.nativeElement.value = '';
    }

    this.itemControl.setValue(null);
  }

  openSelectOptions($event: MouseEvent, trigger: MatAutocompleteTrigger) {
    $event.stopPropagation();
    trigger.openPanel();
  }

  getColor(color?: string) {
    if(!color) {
      return 'white';
    }
    const c = color!.substring(1);      // strip #
    const rgb = parseInt(c, 16);   // convert rrggbb to decimal
    const r = (rgb >> 16) & 0xff;  // extract red
    const g = (rgb >>  8) & 0xff;  // extract green
    const b = (rgb >>  0) & 0xff;  // extract blue

    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
    // console.log('brightness', luma);

    if (luma < 125) {
      // pick a different colour
      return 'white';
    }

    return 'black';
  }
}
